import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiTrash2 } from 'react-icons/fi';
import { useStore } from '../store/useStore';
import { getCountryFromIP, getShippingCost } from '../services/geolocationService';
import '../styles/Cart.css';

export default function Cart() {
  const cart = useStore((state) => state.cart);
  const products = useStore((state) => state.products);
  const removeFromCart = useStore((state) => state.removeFromCart);
  const updateCartQuantity = useStore((state) => state.updateCartQuantity);
  const [promoCode, setPromoCode] = useState('');
  const [countryCode, setCountryCode] = useState(null);

  // Swipe state for mobile cart items
  const [swipeState, setSwipeState] = useState({});
  const swipeStartX = useRef({});
  const swipeStartTime = useRef({});

  // Detect country on component mount
  useEffect(() => {
    const detectCountry = async () => {
      const code = await getCountryFromIP();
      setCountryCode(code);
    };
    detectCountry();
  }, []);

  // Get current price for a cart item (from products store)
  const getCurrentPrice = (cartItem) => {
    const product = products.find(p => p.id === cartItem.id);
    // Use current product price if available, otherwise use stored price
    return product ? parseFloat(product.price) : parseFloat(cartItem.price);
  };

  // Get current discount price for a cart item
  const getCurrentDiscountPrice = (cartItem) => {
    const product = products.find(p => p.id === cartItem.id);
    return product?.discount_price ? parseFloat(product.discount_price) : 0;
  };

  // Calculate subtotal using current prices
  const subtotal = cart.reduce((sum, item) => {
    const currentPrice = getCurrentPrice(item);
    return sum + currentPrice * item.quantity;
  }, 0);
  const shippingInfo = getShippingCost(countryCode, subtotal);
  const shipping = shippingInfo.cost;
  const total = subtotal + shipping;

  const handleQuantityChange = (cartId, quantity) => {
    if (quantity > 0) {
      updateCartQuantity(cartId, quantity);
    }
  };

  // Swipe handlers for mobile cart item deletion
  const handleSwipeStart = (cartId, e) => {
    swipeStartX.current[cartId] = e.touches[0].clientX;
    swipeStartTime.current[cartId] = Date.now();
  };

  const handleSwipeMove = (cartId, e) => {
    if (!swipeStartX.current[cartId]) return;

    const currentX = e.touches[0].clientX;
    const diff = swipeStartX.current[cartId] - currentX;

    // Update swipe offset (negative means swiped left)
    setSwipeState((prev) => ({
      ...prev,
      [cartId]: diff > 0 ? Math.min(diff, 120) : 0
    }));
  };

  const handleSwipeEnd = (cartId) => {
    const offset = swipeState[cartId] || 0;
    const duration = Date.now() - (swipeStartTime.current[cartId] || 0);

    // If swiped more than 60px or fast swipe (less than 300ms and more than 40px), trigger delete
    if (offset > 60 || (duration < 300 && offset > 40)) {
      removeFromCart(cartId);
    }

    // Reset swipe state
    setSwipeState((prev) => ({
      ...prev,
      [cartId]: 0
    }));
    swipeStartX.current[cartId] = undefined;
    swipeStartTime.current[cartId] = undefined;
  };

  const handleSwipeCancel = (cartId) => {
    setSwipeState((prev) => ({
      ...prev,
      [cartId]: 0
    }));
    swipeStartX.current[cartId] = undefined;
    swipeStartTime.current[cartId] = undefined;
  };

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <h1>Shopping Cart</h1>
          <div className="empty-cart">
            <p>Your cart is empty</p>
            <Link to="/products" className="btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <h1>Shopping Cart</h1>

        <div className="cart-content">
          {/* Cart Items */}
          <div className="cart-items">
            <table className="cart-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item) => {
                  const currentPrice = getCurrentPrice(item);
                  const discountPrice = getCurrentDiscountPrice(item);
                  const displayPrice = discountPrice > 0 ? discountPrice : currentPrice;
                  const swipeOffset = swipeState[item.cartId] || 0;

                  return (
                    <tr
                      key={item.cartId}
                      className="cart-row-swipeable"
                      onTouchStart={(e) => handleSwipeStart(item.cartId, e)}
                      onTouchMove={(e) => handleSwipeMove(item.cartId, e)}
                      onTouchEnd={() => handleSwipeEnd(item.cartId)}
                      onTouchCancel={() => handleSwipeCancel(item.cartId)}
                      style={{ '--swipe-offset': `${swipeOffset}px` }}
                    >
                      <td className="product-cell">
                        <img src={item.image} alt={item.name} />
                        <div>
                          <h4>{item.name}</h4>
                          <p>{item.category}</p>
                        </div>
                      </td>
                      <td>
                        <div className="product-price">
                          {discountPrice > 0 ? (
                            <>
                              <span className="original-price">${currentPrice.toFixed(2)}</span>
                              <span className="discounted-price">${displayPrice.toFixed(2)}</span>
                            </>
                          ) : (
                            <span>${currentPrice.toFixed(2)}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity || 1}
                          onChange={(e) =>
                            handleQuantityChange(item.cartId, parseInt(e.target.value))
                          }
                          className="quantity-input"
                        />
                      </td>
                      <td>${(displayPrice * (item.quantity || 1)).toFixed(2)}</td>
                      <td>
                        <button
                          className="btn-remove"
                          onClick={() => removeFromCart(item.cartId)}
                          title="Remove item"
                        >
                          <FiX />
                        </button>
                      </td>
                      {/* Swipe delete indicator - shown on mobile when swiped */}
                      {swipeOffset > 0 && (
                        <td className="swipe-delete-indicator" style={{ transform: `translateX(-${swipeOffset}px)` }}>
                          <FiTrash2 />
                          <span>Delete</span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Link to="/products" className="btn-continue-shopping">
              ← Continue Shopping
            </Link>
          </div>

          {/* Order Summary */}
          <div className="cart-summary">
            <h2>Order Summary</h2>

            <div className="promo-code">
              <input
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              <button className="btn-apply">Apply</button>
            </div>

            <div className="summary-details">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping:</span>
                <span>
                  {shipping === 0 ? (
                    <span className="free">Free</span>
                  ) : (
                    `${shippingInfo.currency === 'EUR' ? '€' : '$'}${shipping.toFixed(2)}`
                  )}
                </span>
              </div>
              <p className="shipping-info">{shippingInfo.description}</p>
              <div className="summary-row total">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <Link to="/checkout" className="btn-checkout">
              Proceed to Checkout
            </Link>

            <div className="payment-methods">
              <p>We accept:</p>
              <div className="payment-icons">
                <img src="https://via.placeholder.com/40?text=BoG" alt="Bank of Georgia" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
